import wretch from 'wretch'
import type { QueryStringAddon as WretchQueryStringAddon } from 'wretch/addons'
import QueryStringAddon from 'wretch/addons/queryString'
import { WretchError } from 'wretch/resolver'

import i18n from '../../i18n'
import { getLoginPage } from '../components/pages/login/utils'
import { classifyFetchError } from '../lib/utils/functions/fetch-error'
import { logout } from './auth/logout'

const APP_VERSION_HEADER = 'X-App-Version'
export const LS_VERSION_KEY = 'appVersion'
const AUTH_ROUTE_PREFIX = '/auth'

/**
 * Compares localStorage appVersion with response header X-App-Version.
 * If different, stores the new version and hard-reloads the page.
 */
const syncAndReloadOnVersionMismatch = (res?: Response) => {
  if (!res?.headers || typeof res.headers.get !== 'function') return
  const headerVersion = res.headers.get(APP_VERSION_HEADER)
  if (!headerVersion) return

  const lsVersion = localStorage.getItem(LS_VERSION_KEY)
  if (!lsVersion) {
    localStorage.setItem(LS_VERSION_KEY, headerVersion)
    return
  }

  if (lsVersion !== headerVersion) {
    localStorage.setItem(LS_VERSION_KEY, headerVersion)
    window.location.reload()
  }
}

declare module 'wretch' {
  interface Wretch {
    post<TRes = unknown, TBody = unknown>(
      this: WretchQueryStringAddon & Wretch<WretchQueryStringAddon, unknown, Promise<unknown>>,
      body?: TBody
    ): Promise<TRes>
    get<TRes = unknown>(
      this: WretchQueryStringAddon & Wretch<WretchQueryStringAddon, unknown, Promise<unknown>>
    ): Promise<TRes>
    put<TRes = unknown, TBody = unknown>(
      this: WretchQueryStringAddon & Wretch<WretchQueryStringAddon, unknown, Promise<unknown>>,
      body?: TBody
    ): Promise<TRes>
    patch<TRes = unknown, TBody = unknown>(
      this: WretchQueryStringAddon & Wretch<WretchQueryStringAddon, unknown, Promise<unknown>>,
      body?: TBody
    ): Promise<TRes>
    delete<TRes = unknown>(
      this: WretchQueryStringAddon & Wretch<WretchQueryStringAddon, unknown, Promise<unknown>>
    ): Promise<TRes>
  }
}

/**
 * "Voir l'espace…" and going back to one's own account afterwards swap the auth cookies for
 * another account's. A request still running for the previous account at that moment is
 * refused (wrong account, or its session is gone) and the user is logged out. So the switch
 * waits for the running requests, and the requests started meanwhile wait for the switch: if it
 * fails they are sent as usual, if it succeeds they never are, as they belong to the session
 * that just ended and the caller reloads the page.
 */
type SessionSwitchRequestInit = RequestInit & {
  // The request that switches the session itself
  sessionSwitch?: boolean
  // Sent even while the session switches (the refresh the switch request may need, the logout)
  bypassSessionSwitch?: boolean
}

let sessionSwitchPromise: Promise<void> | null = null
let pendingRequests = 0
let onPendingRequestsSettled: (() => void)[] = []

// A request with an expired token stays running while it is refreshed and replayed
const waitForPendingRequests = () =>
  pendingRequests === 0
    ? Promise.resolve()
    : new Promise<void>((resolve) => onPendingRequestsSettled.push(resolve))

/**
 * Runs the request that ends the current session and restores another one. It must be sent
 * with `.options({ sessionSwitch: true })`, and the caller must reload the page once it succeeds.
 */
export const switchSession = async <T>(request: () => Promise<T>): Promise<T> => {
  // A second click waits for the running switch: it is never sent if that switch succeeds
  while (sessionSwitchPromise) await sessionSwitchPromise

  let releaseRequests = () => {}
  sessionSwitchPromise = new Promise<void>((resolve) => {
    releaseRequests = resolve
  })

  try {
    await waitForPendingRequests()
    return await request()
  } catch (error) {
    sessionSwitchPromise = null
    releaseRequests()
    throw error
  }
}

// Sends a request once no session switch holds it back, counting it as running until it is done
const trackedFetch = async (
  input: RequestInfo | URL,
  init: SessionSwitchRequestInit | undefined,
  handle: (response: Response) => Promise<Response>
): Promise<Response> => {
  const { sessionSwitch, bypassSessionSwitch } = init ?? {}
  if (!sessionSwitch) {
    if (!bypassSessionSwitch) while (sessionSwitchPromise) await sessionSwitchPromise
    pendingRequests++
  }

  try {
    const response = await fetch(input, init)
    syncAndReloadOnVersionMismatch(response)
    return await handle(response)
  } catch (error) {
    throw classifyFetchError(error, input, init)
  } finally {
    if (!sessionSwitch && --pendingRequests === 0) {
      onPendingRequestsSettled.forEach((resolve) => resolve())
      onPendingRequestsSettled = []
    }
  }
}

// Public pages reachable without a session (the token travels in the URL);
// a 401/403 there is expected and must not bounce the visitor to the login page.
const PUBLIC_PATH_PATTERNS = [/^\/enrollments\/\d+\/(accept|reject)-cse$/]

const isPublicPath = (pathname: string) =>
  PUBLIC_PATH_PATTERNS.some((pattern) => pattern.test(pathname))

/**
 * Drops whatever is left of the session and sends the user back to the login page.
 * Skipped when already on an auth page, otherwise a failing request there would
 * bounce the page in a loop.
 */
const forceLogout = async () => {
  if (window.location.pathname.startsWith(AUTH_ROUTE_PREFIX)) return
  if (isPublicPath(window.location.pathname)) return

  await logout().catch(() => {})
  window.location.assign(getLoginPage(window.location.pathname))
}

const API_URL = (import.meta as unknown as { env: Record<string, string> }).env.VITE_API
const REFRESH_LOCK_NAME = 'lec-refresh-access-token'

/**
 * Every LEC tab of the browser shares the same refresh cookie, and a refresh token can only be
 * used once. Two tabs refreshing at the same moment would send the same token twice and the
 * second refresh would end the session, so the tabs take turns through a browser-wide lock.
 */
const withRefreshLock = async (callback: () => Promise<void>): Promise<void> => {
  if (typeof navigator !== 'undefined' && navigator.locks)
    await navigator.locks.request(REFRESH_LOCK_NAME, callback)
  else await callback()
}

let refreshAccessTokenPromise: Promise<void> | null = null
const refreshAccessToken = async (): Promise<void> => {
  if (!refreshAccessTokenPromise) {
    refreshAccessTokenPromise = withRefreshLock(async () => {
      const response = await trackedFetch(
        `${API_URL}/auth/access-token`,
        { credentials: 'include', bypassSessionSwitch: true },
        (res) => Promise.resolve(res)
      )
      if (!response.ok) throw new Error(`Access token refresh failed (${response.status})`)
    })
      .catch(async (error) => {
        await forceLogout()
        throw error
      })
      .finally(() => {
        refreshAccessTokenPromise = null
      })
  }

  return refreshAccessTokenPromise
}

// An expired access token can be recovered by refreshing it.
const isAccessTokenExpired = async (response: Response): Promise<boolean> => {
  if (response.status !== 401) return false
  const body = (await response
    .clone()
    .json()
    .catch(() => null)) as { error?: string } | null
  return body?.error === 'TOKEN_EXPIRED'
}

/**
 * Refreshes an expired access token and replays the request here rather than in a catcher,
 * so the request stays counted as running until it is done and a session switch never
 * starts in between.
 */
const wrappedFetch: typeof fetch = (input, init) =>
  trackedFetch(input, init, async (response) => {
    if (!(await isAccessTokenExpired(response))) return response
    try {
      await refreshAccessToken()
    } catch {
      return response
    }
    const retriedResponse = await fetch(input, init)
    syncAndReloadOnVersionMismatch(retriedResponse)
    return retriedResponse
  })

export const rawRestApiClient = wretch(API_URL)
  .addon(QueryStringAddon)
  .polyfills({ fetch: wrappedFetch })
  .headers({
    'x-timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
    'Accept-Language': localStorage.getItem('language') ?? i18n.language
  })

// A revoked session, a malformed token or a missing one can never be recovered,
// so the session is over.
const shouldLogout = (err: WretchError): boolean => {
  return err.json?.error === 'TOKEN_INVALID' || err.json?.error === 'TOKEN_MISSING'
}

const baseClient = rawRestApiClient
  .options({ credentials: 'include' })
  // An expired access token is refreshed and the request replayed in wrappedFetch
  .catcher(401, async (err) => {
    if (shouldLogout(err)) await forceLogout()
    throw err
  })
  .catcher(403, async () => {
    await forceLogout()
  })

type ValueType = string | number | boolean | null | ValueType[] | { [key: string]: ValueType }

const roundDecimals = (value: ValueType): unknown => {
  if (typeof value === 'number' && !Number.isInteger(value)) return Math.round(value * 100) / 100
  if (typeof value === 'object' && value !== null) {
    if (Array.isArray(value)) return value.map(roundDecimals)
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, roundDecimals(v)]))
  }
  return value
}

export let restApiClient = baseClient
  .headers({ 'Accept-Language': localStorage.getItem('language') ?? i18n.language })
  .resolve(async (res) => {
    const jsonResponse = await res.json()
    return roundDecimals(jsonResponse as ValueType) // Apply rounding to all decimals in the response
  })

i18n.on('languageChanged', (lng) => {
  restApiClient = restApiClient.headers({ 'Accept-Language': lng })
})

export const restApiClientBlob = baseClient.resolve((res) => res.blob())

// For downloading files
export const restApiClientRes = baseClient.resolve((res) => res)
